import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogContent, MatDialogActions } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { Router, RouterModule } from '@angular/router';
import { FormGroup, FormControl, Validators, ReactiveFormsModule } from '@angular/forms';
import { UserService } from '../service/UserService';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule } from '@angular/common';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'app-coupon-dialog',
  templateUrl: './coupon-dialog.component.html',
  styleUrls: ['./coupon-dialog.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatDialogContent,
    MatDialogActions,
    MatButtonModule,
    RouterModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatProgressSpinnerModule
  ]
})
export class CouponDialogComponent {
  hide = true;
  loadingLogin = false;
  loadingRegister = false;
  message = '';
  error = '';

  contactForm = new FormGroup({
    email: new FormControl('', [Validators.required, Validators.email]),
    password: new FormControl('', Validators.required),
    phone: new FormControl('', Validators.required)
  });

  private readonly WELCOME_PROMO_CODE_ID = 'NEW-CLIENT';

  constructor(
    public dialogRef: MatDialogRef<CouponDialogComponent>,
    private router: Router,
    private userService: UserService,
    @Inject(MAT_DIALOG_DATA) public data: { title: string, message: string, urlImage: string, code: string }
  ) {}

  onClose(): void {
    this.dialogRef.close();
  }

  // Valida que email y password estén llenos (para Login)
  isLoginFormInvalid(): boolean {
    const email = this.contactForm.get('email');
    const password = this.contactForm.get('password');
    return !email?.value || !password?.value;
  }

  // Valida que email, password y phone estén llenos (para Registro)
  isRegisterFormInvalid(): boolean {
    const email = this.contactForm.get('email');
    const password = this.contactForm.get('password');
    const phone = this.contactForm.get('phone');
    return !email?.value || !password?.value || !phone?.value;
  }

  async onRegister(): Promise<void> {
    if (this.contactForm.invalid) {
      this.contactForm.markAllAsTouched(); 
      this.error = 'Todos los campos son obligatorios.'; // Mensaje único
      return;
    }
    const { email, password, phone } = this.contactForm.value;
    const emailStr = (email || '').toString();
    const passwordStr = (password || '').toString();
    const phoneStr = (phone || '').toString();
    this.loadingRegister = true;
    try {
      const response = await this.userService.register(emailStr, passwordStr);
      localStorage.setItem('token', response.data.token);
      // Optionally validate and store user
      const validation = await this.userService.validationLogin(response.data.token);
      localStorage.setItem('cliente', JSON.stringify(validation.data));
      // after register, send phone to addUser microservice if phone provided
      if (phoneStr) {
        const newUser: any = {
          id: validation.data.uid || validation.data._id || '',
          email: emailStr,
          user: validation.data.uid || validation.data._id || '',
          phone: phoneStr
        };
        this.userService.addUser(newUser).subscribe({
          next: (addResponse) => {
            console.log('Usuario agregado al microservicio:', addResponse);
            // ahora guardar la promoción como en el componente de login
            this.userService.saveUserPromotion(validation.data.uid || validation.data._id || '', emailStr, this.WELCOME_PROMO_CODE_ID)
              .subscribe({
                next: () => {
                  console.log('Cupón guardado con éxito.');
                  this.message = '¡Registro exitoso! ¡Ya puedes usar tu cupón de bienvenida!';
                  this.error = '';
                },
                error: (error: HttpErrorResponse) => {
                  if (error.status === 409) {
                    this.message = '¡Registro exitoso! Tu cupón de bienvenida ya estaba en tu cuenta.';
                    this.error = '';
                  } else if (error.status === 200 && error.error instanceof ProgressEvent) {
                    this.message = '¡Registro exitoso! ¡Ya puedes usar tu cupón de bienvenida!';
                    this.error = '';
                  } else {
                    console.error('Error al guardar el cupón', error);
                    this.message = '¡Registro exitoso! Pero no se pudo guardar tu cupón. Intenta más tarde.';
                    this.error = 'error';
                  }
                },
                complete: () => {
                  this.loadingRegister = false;
                  // opcional: cerrar diálogo y navegar a cuenta
                  setTimeout(() => {
                    this.dialogRef.close();
                    this.router.navigate(['/product']);
                  }, 1500);
                }
              });
          },
          error: (addError) => {
            console.error('Error al agregar usuario al microservicio:', addError);
            this.message = '¡Registro exitoso! Pero no se pudo guardar la información del usuario.';
            this.error = 'error';
            this.loadingRegister = false;
            setTimeout(() => {
              this.dialogRef.close();
              this.router.navigate(['/product']);
            }, 1500);
          }
        });
       }
      // si no se proporcionó phone, igualmente cerramos y mostramos mensaje
      if (!phoneStr) {
        this.message = 'Registro exitoso. Revisa tu cuenta.';
        this.loadingRegister = false;
        this.dialogRef.close();
      }
     } catch (err) {
       console.error('Error en registro desde diálogo:', err);
       this.error = 'No se pudo registrar.';
       this.loadingRegister = false;
     }
   }

  async onLoginClick(): Promise<void> {
    if (this.contactForm.get('email')?.invalid || this.contactForm.get('password')?.invalid) {
      return;
    }
    const { email, password } = this.contactForm.value;
    const emailStr = (email || '').toString();
    const passwordStr = (password || '').toString();
    this.loadingLogin = true;
    try {
      const response = await this.userService.login(emailStr, passwordStr);
      localStorage.setItem('token', response.data.token);
      const validation = await this.userService.validationLogin(response.data.token);
      localStorage.setItem('cliente', JSON.stringify(validation.data));
      this.loadingLogin = false;
      this.dialogRef.close();
    } catch (err) {
      console.error('Error en login desde diálogo:', err);
      this.error = 'Usuario o contraseña incorrecto.';
      this.loadingLogin = false;
    }
  }
}